import { useState } from 'react';
import { api, replaceCamera, type Camera, type ReplaceDraft, type TestResult } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Replace camera (§D10.2) — "kayak zfs replace disk gitu".
 *
 * Analoginya dipakai apa adanya: yang diganti **perangkatnya**, yang bertahan
 * **posisinya beserta seluruh riwayatnya**. `id` tidak berubah, `folderName`
 * tidak berubah, dan seluruh rekaman tetap menempel pada kamera yang sama.
 *
 * Kenapa bukan "hapus lalu tambah baru": menghapus kamera membuang seluruh
 * indeks rekaman dan folder rekamannya (§G11) — yaitu justru rekaman minggu lalu
 * yang alasannya kameranya diganti.
 */

const input = 'mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base';

export function ReplaceCameraDialog({
  camera,
  onSelesai,
  onTutup,
}: {
  camera: Camera;
  onSelesai: () => Promise<void> | void;
  onTutup: () => void;
}) {
  const { t } = useBahasa();
  const [draft, setDraft] = useState<ReplaceDraft>({
    host: camera.host ?? '',
    port: camera.port,
    transport: camera.transport,
    mainPath: camera.mainPath,
    subPath: camera.subPath,
    username: camera.username,
  });
  const [password, setPassword] = useState('');
  const [sentuhPassword, setSentuhPassword] = useState(false);
  const [uji, setUji] = useState<TestResult | 'jalan' | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);

  const set = <K extends keyof ReplaceDraft>(k: K, v: ReplaceDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  /**
   * Uji koneksi **wajib ditampilkan, tapi tidak wajib berhasil** (§D10.2
   * langkah 3): kamera pengganti sering dipasang sebelum kabel jaringannya
   * rapi, dan memaksa uji lulus berarti operator tidak bisa menyimpan
   * pekerjaannya sampai orang lain selesai.
   *
   * Ia menguji kamera yang **masih tersimpan**, bukan parameter di form — nilai
   * barunya belum ada di server sampai disimpan. Jadi urutan yang benar:
   * simpan, lalu uji. Tombolnya karena itu berbunyi "simpan lalu uji".
   */
  async function simpan(lanjutUji: boolean) {
    setSibuk(true);
    setGalat(null);
    try {
      await replaceCamera(camera.id, sentuhPassword ? { ...draft, password: password || null } : draft);
      await onSelesai();
      if (lanjutUji) {
        setUji('jalan');
        const hasil = await api<TestResult>(`/cameras/${camera.id}/test?profile=main`, {
          method: 'POST',
        });
        setUji(hasil);
      } else {
        onTutup();
      }
    } catch (e) {
      setGalat((e as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  /** Nilai lama tampil berdampingan sebagai kolom "sebelum" (§D10.2 langkah 2). */
  function Baris({
    label,
    lama,
    children,
  }: {
    label: string;
    lama: string;
    children: React.ReactNode;
  }) {
    return (
      <div className="grid gap-1 sm:grid-cols-[10rem_1fr] sm:items-center sm:gap-3">
        <label className="text-sm">
          {label}
          <span className="block truncate font-mono text-xs text-muted" title={lama}>
            {lama || '—'}
          </span>
        </label>
        {children}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('Replace camera')}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-line bg-surface p-4"
      >
        <h2 className="text-lg">{t('Replace {nama}', { nama: camera.name })}</h2>
        <p className="mt-1 text-sm text-muted">
          {t('The name, the recording folder and every existing recording stay with this camera. Only how Lensa reaches the device changes.')}
        </p>

        {!camera.maintenanceSince && (
          <p className="mt-3 rounded-md border border-amber-500 px-3 py-2 text-xs">
            {t('This camera is not marked as under maintenance. Mark it first so the gap in its recording has a reason written down.')}
          </p>
        )}

        {galat && (
          <p role="alert" className="mt-3 rounded-md border border-red-600 px-3 py-2 text-sm">
            {galat}
          </p>
        )}

        <div className="mt-4 space-y-3">
          <Baris label={t('Host')} lama={camera.host ?? ''}>
            <input className={input} value={draft.host ?? ''} onChange={(e) => set('host', e.target.value)} />
          </Baris>
          <Baris label={t('RTSP port')} lama={String(camera.port)}>
            <input
              className={input}
              type="number"
              value={draft.port ?? 554}
              onChange={(e) => set('port', Number(e.target.value))}
            />
          </Baris>
          <Baris label={t('RTSP transport')} lama={camera.transport.toUpperCase()}>
            <select
              className={input}
              value={draft.transport ?? 'tcp'}
              onChange={(e) => set('transport', e.target.value as 'tcp' | 'udp')}
            >
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
            </select>
          </Baris>
          <Baris label={t('Main stream path')} lama={camera.mainPath}>
            <input
              className={input}
              value={draft.mainPath ?? ''}
              onChange={(e) => set('mainPath', e.target.value)}
            />
          </Baris>
          <Baris label={t('Sub stream path')} lama={camera.subPath ?? ''}>
            <input
              className={input}
              value={draft.subPath ?? ''}
              onChange={(e) => set('subPath', e.target.value || null)}
            />
          </Baris>
          <Baris label={t('Username')} lama={camera.username ?? ''}>
            <input
              className={input}
              value={draft.username ?? ''}
              onChange={(e) => set('username', e.target.value || null)}
            />
          </Baris>
          <Baris label={t('Password')} lama={camera.hasPassword ? t('stored') : t('not set')}>
            <input
              className={input}
              type="password"
              value={password}
              placeholder={t('Leave blank to keep the current one')}
              onChange={(e) => {
                setPassword(e.target.value);
                setSentuhPassword(true);
              }}
            />
          </Baris>
        </div>

        {uji && (
          <p
            className={`mt-3 rounded-md border px-3 py-2 text-sm ${
              uji === 'jalan'
                ? 'border-line text-muted'
                : uji.ok
                  ? 'border-green-600 text-green-600'
                  : 'border-amber-500 text-amber-600'
            }`}
          >
            {uji === 'jalan' ? t('Testing…') : uji.ok ? t('Connected.') : uji.message}
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={sibuk}
            onClick={() => void simpan(true)}
            className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-60"
          >
            {sibuk ? t('Saving…') : t('Save and test')}
          </button>
          <button
            type="button"
            disabled={sibuk}
            onClick={() => void simpan(false)}
            className="min-h-tap rounded-md border border-line px-4 text-base"
          >
            {t('Save without testing')}
          </button>
          <button
            type="button"
            onClick={onTutup}
            className="min-h-tap rounded-md border border-line px-4 text-base"
          >
            {t('Close')}
          </button>
        </div>
      </div>
    </div>
  );
}
